import React, { useState, useEffect, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, FileText, User, DollarSign, Calendar, Eye, Upload, Trash2, Loader2, MessageSquare, Send, Crown, Zap, Star, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { SSNRevealButton } from '@/components/shared/SSNRevealButton';
import { pricingAPI } from '@/services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function TaxFilingDetailView() {
  const { filingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [filing, setFiling] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [estimatedRefund, setEstimatedRefund] = useState('');
  const [savingRefund, setSavingRefund] = useState(false);
  const [balanceDue, setBalanceDue] = useState('');
  const [savingBalanceDue, setSavingBalanceDue] = useState(false);
  const [discountAmount, setDiscountAmount] = useState('');
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [savingPackage, setSavingPackage] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Pricing from API
  const [packagePrices, setPackagePrices] = useState({
    basic: 99,
    standard: 199,
    premium: 349
  });
  
  // Document preview modal state (for agents - no download)
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    documentId: null,
    fileUrl: null,
    fileName: null,
    mimeType: null,
    isAgentDocument: false
  });

  // Get clientId from location state if navigated from client detail page
  const clientId = location.state?.clientId;
  
  // Helper function to get package price
  const getPackagePrice = (packageName) => {
    if (!packageName) return 0;
    return packagePrices[packageName.toLowerCase()] || 0;
  };

  // Fetch pricing plans
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const plans = await pricingAPI.getPlans('tax', true, false);
        if (plans && plans.length > 0) {
          const prices = {};
          // Helper to normalize plan name to key
          const normalizeKey = (name) => {
            const lowerName = name.toLowerCase();
            if (lowerName.includes('basic')) return 'basic';
            if (lowerName.includes('standard')) return 'standard';
            if (lowerName.includes('premium')) return 'premium';
            if (lowerName.includes('advanced')) return 'advanced';
            return lowerName.split(' ')[0];
          };
          plans.forEach(plan => {
            const key = normalizeKey(plan.name);
            prices[key] = plan.price;
          });
          if (Object.keys(prices).length > 0) {
            setPackagePrices(prev => ({ ...prev, ...prices }));
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // Auto-scroll to latest message within container
  const scrollToBottom = () => {
    // Use parent scroll container instead of scrollIntoView to prevent page scroll
    const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  };

  useEffect(() => {
    // Only scroll within message area when messages change
    if (messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/filings/${filingId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${backendUrl}/api/filings/${filingId}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    if (user && filingId) {
      fetchFilingDetail();
      fetchDocuments();
      fetchMessages();
    }
  }, [user, filingId]);

  // Poll for new messages every 10 seconds when filing is not DRAFT
  useEffect(() => {
    if (filing && filing.status !== 'DRAFT') {
      const interval = setInterval(() => {
        fetchMessages();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [filing]);

  const fetchDocuments = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(
        `${backendUrl}/api/documents/tax_filing/${filingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDocuments(response.data);
    } catch (error) {
      console.error('[TaxFilingDetailView] Error fetching documents:', error);
      // Don't show error toast - documents might not exist yet
    }
  };

  const handleViewDocument = async (documentId, fileUrl, fileName, mimeType = null, isAgentDocument = false, isJsonbDocument = false) => {
    // For agents: open in modal (no download option)
    // For admins: open in new tab using proxy (can download)
    const backendUrl = getBackendUrl();
    const token = localStorage.getItem('access_token');
    
    if (user?.role === 'admin') {
      try {
        // Use appropriate endpoint based on document type
        let viewUrl;
        if (isAgentDocument) {
          // Agent documents are stored in JSONB, use special endpoint
          viewUrl = `${backendUrl}/api/files/agent-document/${filingId}/${documentId}`;
        } else if (isJsonbDocument && fileUrl) {
          // JSONB documents (like otherDocuments) - use POST endpoint with URL
          const response = await fetch(`${backendUrl}/api/files/view-by-url`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              file_url: fileUrl,
              entity_type: 'tax_filing',
              entity_id: filingId
            })
          });
          
          if (!response.ok) {
            throw new Error('Failed to load document');
          }
          
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          return;
        } else if (documentId) {
          // Regular documents in documents table - include file_url as fallback
          viewUrl = `${backendUrl}/api/files/view/${documentId}`;
          if (fileUrl) {
            viewUrl += `?file_url=${encodeURIComponent(fileUrl)}`;
          }
        } else {
          // Fallback to direct URL
          viewUrl = fileUrl.startsWith('http') ? fileUrl : `${backendUrl}${fileUrl}`;
        }
        
        const response = await fetch(viewUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load document');
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } catch (error) {
        console.error('Error viewing document:', error);
        toast.error('Failed to load document');
      }
    } else {
      // Agent role - open in modal without download
      setPreviewModal({
        isOpen: true,
        documentId: documentId,
        fileUrl: fileUrl,
        fileName: fileName,
        mimeType: mimeType,
        isAgentDocument: isAgentDocument,
        isJsonbDocument: isJsonbDocument
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const payload = { status: newStatus };
      
      // Include status message when setting to NEED_INFO
      if (newStatus === 'NEED_INFO' && statusMessage.trim()) {
        payload.status_message = statusMessage.trim();
      }
      
      await axios.put(
        `${backendUrl}/api/filings/${filingId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFiling({ ...filing, status: newStatus, status_message: payload.status_message || filing.status_message });
      toast.success('Status updated successfully');
      
      // Clear message after successful update
      if (newStatus === 'NEED_INFO') {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Save estimated refund
  const handleSaveEstimatedRefund = async () => {
    try {
      setSavingRefund(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const refundValue = estimatedRefund ? parseFloat(estimatedRefund) : null;
      
      await axios.patch(
        `${backendUrl}/api/tax-filings/${filingId}`,
        { estimated_refund: refundValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFiling({ ...filing, estimated_refund: refundValue });
      toast.success('Estimated refund saved successfully');
    } catch (error) {
      console.error('Error saving estimated refund:', error);
      toast.error('Failed to save estimated refund');
    } finally {
      setSavingRefund(false);
    }
  };

  // Save balance due
  const handleSaveBalanceDue = async () => {
    try {
      setSavingBalanceDue(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const balanceValue = balanceDue ? parseFloat(balanceDue) : null;
      
      await axios.patch(
        `${backendUrl}/api/tax-filings/${filingId}`,
        { balance_due: balanceValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFiling({ ...filing, balance_due: balanceValue });
      toast.success('Balance due saved successfully');
    } catch (error) {
      console.error('Error saving balance due:', error);
      toast.error('Failed to save balance due');
    } finally {
      setSavingBalanceDue(false);
    }
  };

  // Save discount amount
  const handleSaveDiscount = async () => {
    try {
      setSavingDiscount(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const discountValue = discountAmount ? parseFloat(discountAmount) : null;
      
      await axios.patch(
        `${backendUrl}/api/tax-filings/${filingId}`,
        { discount_amount: discountValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFiling({ ...filing, discount_amount: discountValue });
      toast.success('Discount saved successfully');
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error('Failed to save discount');
    } finally {
      setSavingDiscount(false);
    }
  };

  // Save selected package
  const handleSavePackage = async (packageValue) => {
    try {
      setSavingPackage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      await axios.patch(
        `${backendUrl}/api/tax-filings/${filingId}`,
        { selected_package: packageValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setFiling({ ...filing, selected_package: packageValue });
      setSelectedPackage(packageValue);
      toast.success('Package updated successfully');
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to update package');
    } finally {
      setSavingPackage(false);
    }
  };

  // Agent document upload
  const handleAgentDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingDoc(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${backendUrl}/api/filings/${filingId}/agent-documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update local state with new document
      const newDoc = response.data.document;
      setFiling({
        ...filing,
        agent_documents: [...(filing.agent_documents || []), newDoc],
      });

      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete agent document
  const handleDeleteAgentDocument = async (documentId) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      await axios.delete(
        `${backendUrl}/api/filings/${filingId}/agent-documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setFiling({
        ...filing,
        agent_documents: (filing.agent_documents || []).filter(
          (doc) => doc.id !== documentId
        ),
      });

      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const fetchFilingDetail = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      console.log('[TaxFilingDetailView] Fetching filing:', filingId);
      
      const response = await axios.get(`${backendUrl}/api/filings/${filingId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[TaxFilingDetailView] Response received:', response.status);
      const data = response.data;
      
      // Parse JSON fields if they're strings
      if (typeof data.personal_info === 'string') {
        try {
          data.personal_info = JSON.parse(data.personal_info);
        } catch (e) {
          data.personal_info = {};
        }
      }
      
      if (typeof data.income_details === 'string') {
        try {
          data.income_details = JSON.parse(data.income_details);
        } catch (e) {
          data.income_details = {};
        }
      }
      
      if (typeof data.deductions === 'string') {
        try {
          data.deductions = JSON.parse(data.deductions);
        } catch (e) {
          data.deductions = {};
        }
      }

      console.log('[TaxFilingDetailView] Data processed, setting filing state');
      setFiling(data);
      // Initialize estimated refund if available
      if (data.estimated_refund) {
        setEstimatedRefund(data.estimated_refund.toString());
      }
      // Initialize balance due if available
      if (data.balance_due) {
        setBalanceDue(data.balance_due.toString());
      }
      // Initialize discount amount if available
      if (data.discount_amount) {
        setDiscountAmount(data.discount_amount.toString());
      }
      // Initialize selected package if available
      const pkg = data.selected_package || data.personal_info?.selectedPackage || '';
      setSelectedPackage(pkg);
    } catch (error) {
      console.error('[TaxFilingDetailView] Error fetching filing detail:', error);
      toast.error(error.response?.data?.detail || 'Failed to load filing details');
      setFiling(null);
    } finally {
      console.log('[TaxFilingDetailView] Setting loading to false');
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (clientId) {
      // If we came from a client detail page, go back to that client
      navigate(`/agent/clients/${clientId}`);
    } else {
      // Otherwise go to the clients list page
      navigate('/agent/clients');
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!filing) {
    return (
      <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
        <div className="text-center py-12">
          <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Tax filing not found</h3>
          <Button className="mt-4" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'submitted':
      case 'completed':
        return 'default';
      case 'in_review':
        return 'secondary';
      case 'draft':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Agent', href: '/agent/dashboard' },
          ...(clientId ? [{ label: 'Clients', href: '/agent/clients' }, { label: 'Client Details', href: `/agent/clients/${clientId}` }] : [{ label: 'Clients', href: '/agent/clients' }]),
          { label: 'Tax Filing Details' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Tax Filing Details</h2>
            <p className="text-muted-foreground">{filing.client_name} - {filing.tax_year}</p>
          </div>
          <Button variant="outline" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>

        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{filing.client_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{user?.role === 'admin' ? (filing.client_email || 'N/A') : '••••••@••••.com'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Filing Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Filing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-5">
              <div>
                <p className="text-sm text-muted-foreground">Tax Year</p>
                <p className="font-semibold text-lg">{filing.tax_year}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Status</p>
                <Select value={filing.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUBMITTED">Submitted</SelectItem>
                    <SelectItem value="NEED_INFO">Waiting for Info</SelectItem>
                    <SelectItem value="READY_FOR_REVIEW">Ready for Review</SelectItem>
                    <SelectItem value="SUBMITTED_TO_IRS">Submitted to IRS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Package</p>
                <Select 
                  value={selectedPackage} 
                  onValueChange={handleSavePackage}
                  disabled={savingPackage}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select package" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">
                      <div className="flex items-center gap-2">
                        <Star className="h-3 w-3 text-slate-600" />
                        Basic
                      </div>
                    </SelectItem>
                    <SelectItem value="standard">
                      <div className="flex items-center gap-2">
                        <Zap className="h-3 w-3 text-blue-600" />
                        Standard
                      </div>
                    </SelectItem>
                    <SelectItem value="premium">
                      <div className="flex items-center gap-2">
                        <Crown className="h-3 w-3 text-amber-600" />
                        Premium
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {savingPackage && <p className="text-xs text-muted-foreground mt-1">Saving...</p>}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Payment Status</p>
                {filing.payment_status === 'paid' ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                    <DollarSign className="h-3 w-3 mr-1" />
                    Paid
                    {filing.paid_at && (
                      <span className="ml-1 text-xs">
                        ({new Date(filing.paid_at).toLocaleDateString()})
                      </span>
                    )}
                  </Badge>
                ) : filing.payment_status === 'failed' ? (
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                    Payment Failed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Pending
                  </Badge>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Estimated Refund</p>
                <div className="flex gap-2">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="pl-9 w-[150px]"
                      value={estimatedRefund}
                      onChange={(e) => setEstimatedRefund(e.target.value)}
                    />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={handleSaveEstimatedRefund}
                    disabled={savingRefund}
                  >
                    {savingRefund ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
                {filing.estimated_refund && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Current: ${Number(filing.estimated_refund).toLocaleString()}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Balance Due</p>
                <div className="flex gap-2">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="pl-9 w-[150px]"
                      value={balanceDue}
                      onChange={(e) => setBalanceDue(e.target.value)}
                    />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={handleSaveBalanceDue}
                    disabled={savingBalanceDue}
                  >
                    {savingBalanceDue ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
                {filing.balance_due && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Current: ${Number(filing.balance_due).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Discount and Payment Section - Admin Only */}
            {user?.role === 'admin' && (
              <div className="grid gap-4 md:grid-cols-4 p-4 bg-muted/30 rounded-lg">
                {/* Package Display */}
                {/* Discount Amount */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Discount Amount</p>
                  <div className="flex gap-2">
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="0.00"
                        className="pl-9 w-[150px]"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                      />
                    </div>
                    <Button 
                      size="sm" 
                      onClick={handleSaveDiscount}
                      disabled={savingDiscount}
                    >
                      {savingDiscount ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                  {filing.discount_amount && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Current: ${Number(filing.discount_amount).toLocaleString()}
                    </p>
                  )}
                </div>
                
                {/* Package Price */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Package Price</p>
                  <p className="font-semibold text-lg">
                    ${getPackagePrice(selectedPackage)}
                  </p>
                </div>
                
                {/* Total After Discount */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Total After Discount</p>
                  <p className="font-semibold text-lg text-green-600">
                    ${(() => {
                      const packagePrice = getPackagePrice(selectedPackage);
                      const discount = filing.discount_amount || 0;
                      return Math.max(0, packagePrice - discount).toLocaleString();
                    })()}
                  </p>
                </div>
              </div>
            )}

            {/* Conversation Box - Show for all non-draft statuses */}
            {filing.status !== 'DRAFT' && (
              <div className="p-4 border rounded-lg bg-muted/20 space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <Label className="font-medium">Conversation with Client</Label>
                </div>
                
                <div className="border rounded-lg bg-white dark:bg-gray-800">
                  <ScrollArea className="h-64 p-3">
                    <div className="space-y-3">
                      {messages.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No messages yet. Start a conversation with the client.
                        </p>
                      )}
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            msg.sender_role === 'agent' ? 'justify-end' : 'justify-start'
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-lg p-3",
                              msg.sender_role === 'agent'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            )}
                          >
                            <p className="text-xs font-medium mb-1 opacity-70">
                              {msg.sender_role === 'agent' ? 'You' : msg.sender_name || 'Client'}
                            </p>
                            <p className="text-sm">{msg.message}</p>
                            <p className="text-xs mt-1 opacity-50">
                              {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                  <div className="p-3 border-t">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        disabled={sendingMessage}
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="gap-2"
                      >
                        <Send className="h-4 w-4" />
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Filing Type</p>
                <p className="font-medium capitalize">{filing.filing_type || 'N/A'}</p>
              </div>
              {filing.estimated_refund && (
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Refund</p>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <p className="font-medium text-green-600">${Number(filing.estimated_refund).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {filing.balance_due && (
                <div>
                  <p className="text-sm text-muted-foreground">Balance Due</p>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4 text-red-600" />
                    <p className="font-medium text-red-600">${Number(filing.balance_due).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">
                    {filing.created_at ? new Date(filing.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">
                    {filing.updated_at ? new Date(filing.updated_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {filing.submission_date && (
              <div>
                <p className="text-sm text-muted-foreground">Submission Date</p>
                <p className="font-medium">
                  {new Date(filing.submission_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Personal Information */}
        {filing.personal_info && typeof filing.personal_info === 'object' && Object.keys(filing.personal_info).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {/* Primary SSN with reveal button */}
                {filing.personal_info?.ssn_token && (
                  <SSNRevealButton
                    ssnToken={filing.personal_info.ssn_token}
                    maskedSSN={filing.personal_info.ssn || 'XXX-XX-XXXX'}
                    label="SSN"
                  />
                )}
                
                {/* Spouse SSN with reveal button */}
                {filing.personal_info?.spouseSSN_token && (
                  <SSNRevealButton
                    ssnToken={filing.personal_info.spouseSSN_token}
                    maskedSSN={filing.personal_info.spouseSSN || 'XXX-XX-XXXX'}
                    label="Spouse SSN"
                  />
                )}
                
                {Object.entries(filing.personal_info)
                  .filter(([key, value]) => {
                    // Filter out complex objects and arrays, and empty values
                    if (key === 'dependents' || key === 'documents') return false;
                    // Filter out SSN-related fields (handled separately with reveal button)
                    if (key === 'ssn' || key === 'ssn_token' || key === 'ssn_secured') return false;
                    if (key === 'spouseSSN' || key === 'spouseSSN_token' || key === 'spouseSSN_secured') return false;
                    // Filter out email and phone fields for non-admin users
                    if (user?.role !== 'admin') {
                      if (key.toLowerCase().includes('email') || key.toLowerCase().includes('phone') || key.toLowerCase().includes('mobile')) return false;
                    }
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => {
                    // Format boolean values
                    let displayValue = value;
                    if (typeof value === 'boolean') {
                      displayValue = value ? 'Yes' : 'No';
                    }
                    
                    return (
                      <div key={key}>
                        <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="font-medium">{displayValue || 'N/A'}</p>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dependents Information */}
        {filing.personal_info?.dependents && Array.isArray(filing.personal_info.dependents) && filing.personal_info.dependents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Dependents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filing.personal_info.dependents.map((dependent, index) => (
                  <div key={dependent.id || index} className="p-4 border rounded-lg">
                    <p className="font-semibold mb-2">Dependent {index + 1}</p>
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{dependent.firstName} {dependent.lastName}</p>
                      </div>
                      {dependent.ssn_token ? (
                        <SSNRevealButton
                          ssnToken={dependent.ssn_token}
                          maskedSSN={dependent.ssn || 'XXX-XX-XXXX'}
                          label="SSN/ITIN"
                          className="text-sm"
                        />
                      ) : (
                        <div>
                          <p className="text-xs text-muted-foreground">SSN/ITIN</p>
                          <p className="text-sm">{dependent.ssn || 'N/A'}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground">Date of Birth</p>
                        <p className="text-sm">{dependent.dob || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Relationship</p>
                        <p className="text-sm capitalize">{dependent.relationship?.replace(/_/g, ' ') || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income Details */}
        {filing.income_details && typeof filing.income_details === 'object' && Object.keys(filing.income_details).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Income Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(filing.income_details)
                  .filter(([key, value]) => {
                    // Filter out complex objects and arrays
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">
                        {typeof value === 'number' ? `$${value.toFixed(2)}` : value || 'N/A'}
                      </p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deductions */}
        {filing.deductions && typeof filing.deductions === 'object' && Object.keys(filing.deductions).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Deductions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(filing.deductions)
                  .filter(([key, value]) => {
                    // Filter out the documents object and other complex types
                    if (key === 'documents') return false;
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">
                        {typeof value === 'number' ? `$${value.toFixed(2)}` : value || 'N/A'}
                      </p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Documents */}
        {documents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Uploaded Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {documents.map((doc) => {
                  const backendUrl = getBackendUrl();
                  const fileTypeLabel = doc.file_type.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                  
                  return (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded hover:border-primary transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <p className="text-xs text-muted-foreground capitalize">{fileTypeLabel}</p>
                          <p className="text-sm font-medium">{doc.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                            {doc.file_size ? ` • ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleViewDocument(doc.id, doc.file_url, doc.file_name, doc.mime_type)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            const backendUrl = getBackendUrl();
                            const token = localStorage.getItem('access_token');
                            const downloadUrl = `${backendUrl}/api/files/view/${doc.id}`;
                            fetch(downloadUrl, {
                              headers: { Authorization: `Bearer ${token}` }
                            })
                              .then(res => res.blob())
                              .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = doc.file_name || 'document';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              })
                              .catch(() => toast.error('Failed to download document'));
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Other Documents Uploaded by Client */}
        {((filing?.deductions?.otherDocuments && filing.deductions.otherDocuments.length > 0) ||
          (filing?.personal_info?.otherDocuments && filing.personal_info.otherDocuments.length > 0)) && (
          <Card>
            <CardHeader>
              <CardTitle>Other Documents</CardTitle>
              <p className="text-sm text-muted-foreground">Additional documents uploaded by client</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(filing.deductions?.otherDocuments || filing.personal_info?.otherDocuments || []).map((doc, index) => (
                  <div key={doc.id || index} className="flex items-center justify-between p-3 border rounded hover:border-primary transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium">{doc.file_name || doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                          {doc.size ? ` • ${(doc.size / 1024).toFixed(0)} KB` : ''}
                          {doc.file_size ? ` • ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    {doc.file_url && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleViewDocument(doc.id, doc.file_url, doc.file_name || doc.filename, doc.mime_type, false, true)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            const backendUrl = getBackendUrl();
                            const token = localStorage.getItem('access_token');
                            fetch(`${backendUrl}/api/files/view-by-url`, {
                              method: 'POST',
                              headers: { 
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ file_url: doc.file_url })
                            })
                              .then(res => res.blob())
                              .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = doc.file_name || doc.filename || 'document';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              })
                              .catch(() => toast.error('Failed to download document'));
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {filing.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Agent Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{filing.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Agent Document Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Documents for Client
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Upload documents for the client to view (supports multiple documents)
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show all uploaded documents */}
            {filing.agent_documents && filing.agent_documents.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Uploaded Documents ({filing.agent_documents.length})
                </p>
                {filing.agent_documents.map((doc, index) => (
                  <div key={doc.id || index} className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-900/20">
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.uploaded_at
                            ? new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => handleViewDocument(doc.id, doc.path, doc.name, doc.mime_type, true)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          const backendUrl = getBackendUrl();
                          const token = localStorage.getItem('access_token');
                          // Open download URL in new window with auth
                          const downloadUrl = `${backendUrl}/api/files/agent-document/${filing.id}/${doc.id}/download`;
                          fetch(downloadUrl, {
                            headers: { Authorization: `Bearer ${token}` }
                          })
                            .then(res => res.blob())
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.name || 'document';
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              a.remove();
                            })
                            .catch(() => toast.error('Failed to download document'));
                        }}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={async () => {
                          try {
                            const backendUrl = getBackendUrl();
                            const token = localStorage.getItem('access_token');
                            await axios.delete(
                              `${backendUrl}/api/filings/${filing.id}/agent-documents/${doc.id}`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success('Document deleted');
                            fetchFiling();
                          } catch (err) {
                            toast.error('Failed to delete document');
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload new document button */}
            <div className="flex items-center gap-4 pt-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAgentDocumentUpload}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingDoc}
                variant={filing.agent_documents?.length > 0 ? "outline" : "default"}
                className="gap-2"
              >
                {uploadingDoc ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {filing.agent_documents?.length > 0 ? 'Add Another Document' : 'Upload Document'}
                  </>
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                PDF, JPG, PNG, WEBP, DOC, DOCX (max 10MB)
              </span>
            </div>
            
            {/* Empty state */}
            {(!filing.agent_documents || filing.agent_documents.length === 0) && (
              <div className="text-center py-6 text-muted-foreground border rounded-lg bg-muted/20">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No documents uploaded yet</p>
                <p className="text-xs mt-1">Upload documents for the client to view</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Document Preview Modal - for agents (no download) */}
      <DocumentPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, documentId: null, fileUrl: null, fileName: null, mimeType: null, isAgentDocument: false })}
        documentId={previewModal.documentId}
        fileUrl={previewModal.fileUrl}
        fileName={previewModal.fileName}
        mimeType={previewModal.mimeType}
        isAgentDocument={previewModal.isAgentDocument}
        filingId={filingId}
      />
    </DashboardLayout>
  );
}
